import "./ProgressGauge.css";

const SECTIONS = [
  {
    label: "Foundation",
    color: "#C97800",
    icon: process.env.PUBLIC_URL + "/Assets/Scoreboard/foundation.svg",
  },
  {
    label: "AI Aware",
    color: "#F5BE1F",
    icon: process.env.PUBLIC_URL + "/Assets/Scoreboard/ai_aware.svg",
  },
  {
    label: "AI Practitioner",
    color: "#39D0A3",
    icon: process.env.PUBLIC_URL + "/Assets/Scoreboard/ai_practitioner.svg",
  },
  {
    label: "AI Strategist",
    color: "#66C2BC",
    icon: process.env.PUBLIC_URL + "/Assets/Scoreboard/ai_strategist.svg",
  },
  {
    label: "AI Native",
    color: "#7B00D4",
    icon: process.env.PUBLIC_URL + "/Assets/Scoreboard/ai_native.svg",
  },
];

const WIDTH = 800;
const HEIGHT = 430;

const CX = 400;
const CY = 420;

const OUTER_R = 350;
const INNER_R = 225;

const SCORE_R = 142;

const MAX_SCORE = 500;

function polarToCartesian(cx, cy, r, angle) {
  const rad = (Math.PI / 180) * angle;

  return {
    x: cx + r * Math.cos(rad),
    y: cy - r * Math.sin(rad),
  };
}

function describeArc(cx, cy, outerR, innerR, startAngle, endAngle) {
  const outerStart = polarToCartesian(cx, cy, outerR, startAngle);
  const outerEnd = polarToCartesian(cx, cy, outerR, endAngle);
  const innerStart = polarToCartesian(cx, cy, innerR, startAngle);
  const innerEnd = polarToCartesian(cx, cy, innerR, endAngle);

  const largeArcFlag = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;

  return `
    M ${outerStart.x} ${outerStart.y}
    A ${outerR} ${outerR} 0 ${largeArcFlag} 1 ${outerEnd.x} ${outerEnd.y}
    L ${innerEnd.x} ${innerEnd.y}
    A ${innerR} ${innerR} 0 ${largeArcFlag} 0 ${innerStart.x} ${innerStart.y}
    Z
  `;
}

function describeStrokeArc(cx, cy, r, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, r, startAngle);
  const end = polarToCartesian(cx, cy, r, endAngle);
  const largeArcFlag = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;

  return `
    M ${start.x} ${start.y}
    A ${r} ${r} 0 ${largeArcFlag} 1 ${end.x} ${end.y}
  `;
}

function pointOnArc(angle, radius) {
  return polarToCartesian(CX, CY, radius, angle);
}

export default function ProgressGauge({ score = 500 }) {
  const safeScore = Math.max(0, Math.min(score, MAX_SCORE));

  const segmentSize = 180 / SECTIONS.length;
  const sectionGap = 1;

  const needleAngle = 180 - (safeScore / MAX_SCORE) * 180;
  const needleEnd = pointOnArc(needleAngle, 210);

  return (
    <div className="progressGauge">
      <svg
        className="progressGaugeSvg"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label={`Progress score ${safeScore}`}
      >
        <defs>
          <filter id="iconGlow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="5" result="blur" />
            <feColorMatrix
              in="blur"
              type="matrix"
              values="
        1 0 0 0 1
        0 1 0 0 1
        0 0 1 0 1
        0 0 0 0.45 0
      "
              result="whiteGlow"
            />
            <feMerge>
              <feMergeNode in="whiteGlow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <linearGradient
            id="scoreGradient"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stopColor="#9B00E8" />
            <stop offset="100%" stopColor="#7900D4" />
          </linearGradient>

          <linearGradient
            id="progressArcGradient"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor="#8B28FF" />
            <stop offset="55%" stopColor="#5EB7FF" />
            <stop offset="100%" stopColor="#66C2BC" />
          </linearGradient>
        </defs>

        {/* Grey base behind colored sections, visible as separators */}
        <path
          d={describeArc(CX, CY, OUTER_R, INNER_R, 180, 0)}
          fill="#ffffff"
        />

        {/* Colored sections */}
        {SECTIONS.map((section, index) => {
          const startAngle = 180 - index * segmentSize - sectionGap;
          const endAngle = 180 - (index + 1) * segmentSize + sectionGap;

          return (
            <path
              key={section.label}
              d={describeArc(CX, CY, OUTER_R, INNER_R, startAngle, endAngle)}
              fill={section.color}
            />
          );
        })}

        {/* Progress arc between sections and score dome */}
        <path
          d={describeStrokeArc(CX, CY, 198, 180, 0)}
          className="progressArcTrack"
        />

        {safeScore > 0 && (
          <path
            d={describeStrokeArc(CX, CY, 198, 180, needleAngle)}
            className="progressArcFill"
          />
        )}

        {/* Section labels */}
        {SECTIONS.map((section, index) => {
          const midAngle = 180 - index * segmentSize - segmentSize / 2;

          const labelPoint = pointOnArc(midAngle, 292);
          const valuePoint = pointOnArc(midAngle, 262);

          const textRotation = 90 - midAngle;

          return (
            <g key={`${section.label}-text`}>
              <text
                x={labelPoint.x}
                y={labelPoint.y}
                className="gaugeLabel"
                textAnchor="middle"
                dominantBaseline="middle"
                transform={`rotate(${textRotation} ${labelPoint.x} ${labelPoint.y})`}
              >
                {section.label}
              </text>

              <text
                x={valuePoint.x}
                y={valuePoint.y}
                className="gaugeValue"
                textAnchor="middle"
                dominantBaseline="middle"
                transform={`rotate(${textRotation} ${valuePoint.x} ${valuePoint.y})`}
              >
                100
              </text>
            </g>
          );
        })}

        {/* Icon circles */}
        {SECTIONS.map((section, index) => {
          const midAngle = 180 - index * segmentSize - segmentSize / 2;
          const iconPoint = pointOnArc(midAngle, 345);

          return (
            <g key={`${section.label}-icon`} filter="url(#iconGlow)">
              <circle
                cx={iconPoint.x}
                cy={iconPoint.y}
                r="20"
                fill={section.color}
                stroke="#ffffff"
                strokeWidth="8"
              />

              <image
                href={section.icon}
                x={iconPoint.x - 10}
                y={iconPoint.y - 10}
                width="20"
                height="20"
                preserveAspectRatio="xMidYMid meet"
              />
            </g>
          );
        })}

        {/* Score dome */}
        <path
          d={`
            M ${CX - SCORE_R} ${CY}
            A ${SCORE_R} ${SCORE_R} 0 0 1 ${CX + SCORE_R} ${CY}
            L ${CX + SCORE_R} ${HEIGHT}
            L ${CX - SCORE_R} ${HEIGHT}
            Z
          `}
          fill="url(#scoreGradient)"
        />

        {/* Needle shadow */}
        <line
          x1={CX + 6}
          y1={CY + 5}
          x2={needleEnd.x + 6}
          y2={needleEnd.y + 5}
          className="gaugeNeedleShadow"
        />

        {/* Needle */}
        <line
          x1={CX}
          y1={CY}
          x2={needleEnd.x}
          y2={needleEnd.y}
          className="gaugeNeedle"
        />

        {/* Smaller circular needle end */}
        <circle cx={CX} cy={CY} r="20" fill="#050505" />

        {/* Score text */}
        <text
          x={CX}
          y="355"
          className="scoreNumber"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {safeScore}
        </text>

        <text
          x={CX}
          y="393"
          className="scoreText"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          Your Score
        </text>
      </svg>
    </div>
  );
}
